<template>
  <template v-if="loading">
    <RemarkV2Skeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
  </template>
  <template v-else>
    <InboxV2Empty v-if="emptyComms" />
    <div v-if="hasUnread" class="tw-flex tw-flex-col tw-pb-4 tw-px-4">
      <div
        class="
          tw-h-16
          tw-sticky
          tw-top-0
          tw-bg-sf-snow-lighter
          tw-z-50
          tw-flex
          tw-flex-row
          tw-items-end
        "
      >
        <span
          class="
            tw-font-medium
            tw-text-lg
            tw-py-2
            tw-flex
            tw-flex-row
            tw-w-full
            tw-items-baseline
            tw-justify-between
          "
        >
          <span>{{ I18n.t('communication.inbox.unread_messages') }}</span>
          <span>
            <span
              class="
                tw-uppercase
                tw-font-medium
                tw-text-xs
                tw-text-sf-gray-dark
                tw-p-2
              "
            >
              {{ totalUnreadMessagesCopy }}
            </span>
            <SfButtonInverted
              small
              inline
              variant="dark"
              :disabled="markingAsRead"
              @click="markAllAsRead"
            >
              <span
                data-testid="firm-inbox-v2-mark-all-as-read-start"
                class="tw-flex tw-flex-row tw-align-baseline tw-gap-2"
              >
                <i class="icon icon-envelope-open" />
                {{ I18n.t('communication.inbox.mark_all_as_read') }}
              </span>
            </SfButtonInverted>
          </span>
        </span>
      </div>
      <div class="tw-gap-4 tw-flex tw-flex-col">
        <InboxNotificationGroup
          v-if="!!groupedUnread['Company']"
          :name="I18n.t('company.start')"
          :number-of-messages="
            groupedUnread['Company']['Client overview'].length
          "
          show-mark-as-read
          :disable-mark-as-read="markingAsRead"
          @mark-as-read="markCategoryAsRead('Company')"
        >
          <template
            v-for="comm in groupedUnread['Company']['Client overview']"
            :key="`${comm.kind}-${comm.id}`"
          >
            <RemarkV2
              v-if="comm.kind === 'remark-card'"
              :remark="comm"
              show-context-btn
              show-period
              show-tags
              part-of="inbox"
            />
            <NotificationContent
              v-else
              type="CheckList"
              :communication-item="comm"
            />
          </template>
        </InboxNotificationGroup>
        <InboxNotificationGroup
          v-for="section in unreadSections"
          :key="section.categoryId"
          :name="section.categoryName"
          :number-of-messages="section.count"
          show-mark-as-read
          :disable-mark-as-read="markingAsRead"
          @mark-as-read="markCategoryAsRead(section.categoryId)"
        >
          <template
            v-for="item in Object.keys(groupedUnread[section.categoryId])"
            :key="`${section.categoryName}-${item}`"
          >
            <template
              v-for="comm in groupedUnread[section.categoryId][item]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkV2
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-context
                show-period
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
        </InboxNotificationGroup>
        <div
          class="
            tw-mx-auto
            tw-my-4
            tw-font-medium
            tw-text-sm
            tw-text-sf-gray-light
            tw-text-center
          "
        >
          {{ I18n.t('communication.messages.no_more_unread_messages') }}
          <SfButtonInverted
            small
            inline
            variant="dark"
            :disabled="markingAsRead"
            @click="markAllAsRead"
          >
            <span
              data-testid="firm-inbox-v2-mark-all-as-read"
              class="tw-flex tw-flex-row tw-align-baseline tw-gap-2"
            >
              <i class="icon icon-envelope-open" />
              {{ I18n.t('communication.inbox.mark_all_as_read') }}
            </span>
          </SfButtonInverted>
        </div>
      </div>
    </div>
    <div v-if="hasRead" class="tw-flex tw-flex-col tw-pb-4 tw-px-4">
      <div
        class="
          tw-h-16
          tw-sticky
          tw-top-0
          tw-bg-sf-snow-lighter
          tw-z-50
          tw-flex
          tw-flex-row
          tw-items-end
        "
      >
        <span
          class="
            tw-font-medium
            tw-text-lg
            tw-py-2
            tw-flex
            tw-flex-row
            tw-w-full
            tw-items-baseline
            tw-justify-between
          "
        >
          {{ I18n.t('communication.inbox.read_messages') }}
        </span>
      </div>
      <div class="tw-gap-4 tw-flex tw-flex-col">
        <InboxNotificationGroup
          v-if="!!groupedRead['Company']"
          :name="I18n.t('company.start')"
          :number-of-messages="groupedRead['Company']['Client overview'].length"
          :show-mark-as-read="false"
          disable-mark-as-read
        >
          <template
            v-for="comm in groupedRead['Company']['Client overview']"
            :key="`${comm.kind}-${comm.id}`"
          >
            <RemarkV2
              v-if="comm.kind === 'remark-card'"
              :remark="comm"
              show-context-btn
              show-context
              show-period
              show-tags
              part-of="inbox"
            />
            <NotificationContent
              v-else
              type="CheckList"
              :communication-item="comm"
            />
          </template>
        </InboxNotificationGroup>
        <InboxNotificationGroup
          v-for="section in readSections"
          :key="section.categoryId"
          :name="section.categoryName"
          :number-of-messages="section.count"
          :show-mark-as-read="false"
          disable-mark-as-read
        >
          <template
            v-for="item in Object.keys(groupedRead[section.categoryId])"
            :key="`${section.categoryName}-${item}`"
          >
            <template
              v-for="comm in groupedRead[section.categoryId][item]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkV2
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-context
                show-period
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
        </InboxNotificationGroup>
      </div>
      <div
        class="
          tw-mx-auto
          tw-my-4
          tw-font-medium
          tw-text-sm
          tw-text-sf-gray-light
          tw-text-center
        "
      >
        {{ I18n.t('communication.messages.no_more_messages') }}
      </div>
    </div>
  </template>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, onBeforeUnmount, onMounted } from 'vue'

import InboxNotificationGroup from '@app/application/communication/screens/inbox/components/inbox-notification-group.vue'
import RemarkV2 from '@app/shared/vue/components/molecules/remark-v2/remark-v2.vue'
import NotificationContent from '@app/application/communication/screens/inbox/components/notification-content.vue'
import SfButtonInverted from '../../../../../shared/vue/components/sf-button/sf-button-inverted.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import RemarkV2Skeleton from '@app/shared/vue/components/molecules/remark-v2/remark-v2-skeleton.vue'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'
import InboxV2Empty from '@app/application/communication/screens/inbox/components/inbox-v2-empty.vue'

const store = useStore()
const I18n = useI18n()

const {
  groupedUnread,
  groupedRead,
  groupedCommunication,
  markAllAsRead,
  markCategoryAsRead,
} = useGroupedFilteredCommunications()

const markingAsRead = computed(() => store.state.sections.markingAsRead)

const emptyComms = computed(() => {
  return Object.keys(groupedCommunication.value).length === 0
})

const emptyNotifications = computed(() => {
  return Object.keys(store.state.notifications.notifications).length === 0
})

const totalUnreadMessagesCopy = computed(() => {
  const totalUnread =
    unreadSections.value.reduce((acc, item) => acc + item.count, 0) +
    (groupedUnread.value['Company']?.['Client overview']?.length || 0)
  return I18n.t('communication.inbox.messages', { count: totalUnread })
})

const loading = computed(
  () =>
    store.getters.loadingCompanies ||
    (emptyNotifications.value && store.getters.loadingNotifications) ||
    store.getters.loadingWorkflows ||
    (emptyComms.value && store.getters.fetchingCommunicationsInProgress)
)

const hasUnread = computed(() => Object.keys(groupedUnread.value).length > 0)

const hasRead = computed(() => Object.keys(groupedRead.value).length > 0)

const unreadSections = computed(() => {
  const result = []
  for (const section of store.getters.sections) {
    if (groupedUnread.value[section.categoryId]) {
      const count = Object.values(
        groupedUnread.value[section.categoryId] || {}
      ).reduce((acc, item) => acc + item.length, 0)
      result.push({ ...section, count })
    }
  }
  return result
})

const readSections = computed(() => {
  const result = []
  for (const section of store.getters.sections) {
    if (groupedRead.value[section.categoryId]) {
      const count = Object.values(
        groupedRead.value[section.categoryId] || {}
      ).reduce((acc, item) => acc + item.length, 0)
      result.push({ ...section, count })
    }
  }
  return result
})

function refreshCommunicationData() {
  store.dispatch('fetchCommunication')
}

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})
</script>

<style scoped>
.inbox-notification-group {
  --sf-inbox-notification-group-sticky-offset: 64px;
}
</style>
